import "../i18nFallback";
import React, { useState, useEffect, useRef, createRef } from 'react';
import Cookies from 'js-cookie';
import ReCAPTCHA from "react-google-recaptcha";

import DynamicLabelInput from './DynamicLabelInput';
import SubscribeListContainer from './SubscribeListContainer';
import { isValidEmail } from '../util/isValidEmail';
import { isInDarkMode } from "../util/isInDarkMode";

const lang = document.documentElement.lang;



function SubscribeForm({initialDisplayLists = false, displayH3 = true}) {
    const [displayLists, setDisplayLists] = useState(initialDisplayLists);
    const [signupDisabled, setSignupDisabled] = useState(true);
    const [formSuccess, setFormSuccess] = useState(false);
    const [formErrors, setFormErrors] = useState([]);
    // Current error message for each field, or null when the field is valid.
    const [fieldErrors, setFieldErrors] = useState({
      firstname: null,
      e_mail: null,
      permission: null,
    });
    // Tracks which fields the user has left at least once, so errors don't show before they've tried.
    const [touchedFields, setTouchedFields] = useState({
      firstname: false,
      e_mail: false,
    });
    const [formData, setFormData] = useState({
        firstname: '',
        e_mail: '',
        lists: [],
        permission: true,
        recaptcha: '',
    });
    reCaptchaRef = createRef();
    const reCaptchaPublicKey = window.RECAPTCHA_PUBLIC_KEY;
    const csrftoken = Cookies.get('csrftoken');
    const darkMode = isInDarkMode();
    // Translated field labels, shared by the inputs and the server error list.
    const firstNameLabel = window.gettext("First Name");
    const emailLabel = window.gettext("Email");
    // Ids of each field's error message, which the input's aria-describedby points to.
    const firstNameErrorId = "firstname-error";
    const emailErrorId = "email-error";
    const permissionErrorId = "permission-error";

    // Returns the name error message, or null when a non-blank name is entered.
    function validateFirstName(value) {
      if ((value || "").trim() === "") {
        return window.gettext("Enter your first name.");
      }
      return null;
    }
  
    // Returns the email error message, or null when the address matches isValidEmail.
    function validateEmail(value) {
      if (!isValidEmail((value || "").trim())) {
        return window.gettext("Enter an email address in the format name@example.com.");
      }
      return null;
    }

    // Returns the consent error message, or null when the box is checked.
    function validatePermission(checked) {
      if (!checked) {
        return window.gettext("To subscribe, check the box to agree to receive these resources.");
      }
      return null;
    }
    const subscribeCopy = (() => {
        switch (lang) {
            case "en-za":
                return {
                    headingHtml: "Stay Connected with <em>Revive Our Hearts South Africa</em>.",
                    updatesHtml: "All signups will receive news and updates from <em>Revive&nbsp;Our&nbsp;Hearts South Africa</em>.<br />Select any additional updates that you'd like to&nbsp;receive.",
                    agreementHtml: "<strong>Yes</strong>, I want to receive these resources from <em>Revive Our Hearts South Africa</em>. By signing up for these lists, you are agreeing to our <a href=\"/privacy-statement/\" target=\"_blank\">privacy policy</a> and <a href=\"/terms-of-service/\" target=\"_blank\">terms of service</a>."
                };
            case "fr":
                return {
                    headingHtml: window.gettext("Stay Connected with <em>Revive Our Hearts</em>."),
                    updatesHtml: null,
                    agreementHtml: window.gettext("<strong>Yes</strong>, I want to receive these resources from <em>Revive Our Hearts</em>. By signing up for these lists, you are agreeing to our <a href=\"/privacy-statement/\" target=\"_blank\">privacy policy</a> and <a href=\"/terms-of-service/\" target=\"_blank\">terms of service</a>.")
                };
            case "de":
                return {
                    headingHtml: window.gettext("Bleib mit <em>Belebe unsere Herzen</em> in Kontakt."),
                    updatesHtml: null,
                    agreementHtml: window.gettext("<strong>Yes</strong>, I want to receive these resources from <em>Revive Our Hearts</em>. By signing up for these lists, you are agreeing to our <a href=\"/privacy-statement/\" target=\"_blank\">privacy policy</a> and <a href=\"/terms-of-service/\" target=\"_blank\">terms of service</a>."),
                    emailLabel: "E-Mail",
                };
            default:
                return {
                    headingHtml: window.gettext("Stay Connected with <em>Revive Our Hearts</em>."),
                    updatesHtml: window.gettext("All signups will receive news and updates from <em>Revive&nbsp;Our&nbsp;Hearts</em>.<br />Select any additional updates that you'd like to&nbsp;receive."),
                    agreementHtml: window.gettext("<strong>Yes</strong>, I want to receive these resources from <em>Revive Our Hearts</em>. By signing up for these lists, you are agreeing to our <a href=\"/privacy-statement/\" target=\"_blank\">privacy policy</a> and <a href=\"/terms-of-service/\" target=\"_blank\">terms of service</a>.")
                };
        }
    })();



  // Wrapper of the thank-you message, used to move focus to its heading after a successful signup.
  const successRef = useRef(null);

  // Moves focus to the thank-you heading once it replaces the form, so screen readers read it and keyboard users aren't lost.
  useEffect(() => {
    if (!formSuccess) return;
    // Falls back to the wrapper if a translation leaves out the <h3>.
    const heading = successRef.current?.querySelector("h3") || successRef.current;
    if (heading) {
      // tabindex -1 lets the heading take focus without adding it to the Tab order.
      heading.setAttribute("tabindex", "-1");
      heading.focus();
    }
  }, [formSuccess]);

  // checks if lists can be displayed
  // Uses the same validators as the error messages, so the button and the messages always agree.
  useEffect(() => {
    if (!validateFirstName(formData.firstname) && !validateEmail(formData.e_mail) && !validatePermission(formData.permission)) {
      setSignupDisabled(false);
    } else {
      setSignupDisabled(true);
    }
  }, [formData]);


  function handleChange(event) {
    let { name, value } = event.target;
    if (name === "permission") {
      value = event.target.checked;
    }
    const updatedFormData = {
      ...formData,
      [name]: value,
    };
    setFormData(updatedFormData);
    // Unchecking consent shows its error right away (checking it again clears it).
    if (name === "permission") {
      setFieldErrors((prevErrors) => ({
        ...prevErrors,
        permission: validatePermission(value),
      }));
    }
    // Once a field has been left, re-check it on every keystroke so its error clears as soon as it's fixed.
    if (name === "firstname" && touchedFields.firstname) {
      setFieldErrors((prevErrors) => ({
        ...prevErrors,
        firstname: validateFirstName(updatedFormData.firstname),
      }));
    }
    if (name === "e_mail" && touchedFields.e_mail) {
      setFieldErrors((prevErrors) => ({
        ...prevErrors,
        e_mail: validateEmail(updatedFormData.e_mail),
      }));
    }
  }

  // When the user leaves the name or email field, mark it touched and show its error, if any.
  function handleBlur(event) {
    const { name, value } = event.target;
    // Only the name and email fields are validated on blur.
    if (name !== "firstname" && name !== "e_mail") return;

    setTouchedFields((prevTouched) => ({
      ...prevTouched,
      [name]: true,
    }));

    if (name === "firstname") {
      setFieldErrors((prevErrors) => ({
        ...prevErrors,
        firstname: validateFirstName(value),
      }));
    }

    if (name === "e_mail") {
      setFieldErrors((prevErrors) => ({
        ...prevErrors,
        e_mail: validateEmail(value),
      }));
    }
  }


  function handleCheckboxChange(event) {
    const { name, value, checked } = event.target;
    setFormData(prevFormData => {
      const updatedLists = checked 
        ? [...prevFormData.lists, value] 
        : prevFormData.lists.filter(item => item !== value);

      return {
        ...prevFormData,
        lists: updatedLists
      };
    });
  }


  function signupClickHandler() {
    setDisplayLists(true);
  }

  async function formSubmitHandler(event) {
    event.preventDefault();

	const extraTags = window.SITE_ACRONYM == "ROH" ? ["New Subscriber"] : [];
	const tags = formData.lists.concat(extraTags);
    // Clears old server errors first, so the same error is announced again if the next attempt fails too.
    setFormErrors([]);
    try {
      const response = await fetch("/mailinglists/_api/create-contact/", {
        method: "POST",
        body: JSON.stringify({
          email: formData.e_mail, 
          first_name: formData.firstname, 
          last_name: "",
          tags: tags,
		      captcha: formData.recaptcha,
        }), 
        headers: {
          "Content-Type": "application/json", 
          "X-CSRFToken": csrftoken,
        }
      });

      if (!response.ok) {
        const errorResponse = await response.json();
        let errorMessages = [];
        if (errorResponse.first_name) {
          errorMessages.push(`${window.gettext("First Name")}: ${errorResponse.first_name[0]}`);
        }
        if (errorResponse.email) {
          errorMessages.push(`${window.gettext("Email")}: ${errorResponse.email[0]}`);
        }
        if (errorResponse.captcha) {
          errorMessages.push(`Captcha: ${errorResponse.captcha[0]}`);
        }
        setFormErrors(errorMessages);
      } else {
        setFormSuccess(true);
        setFormErrors([]);
      }
      
    } catch (error) {
      console.log("formSubmitHandler error: " + error);
      setFormSuccess(false);
    }
  }

  function onChangeReCaptcha(value) {
    const updatedFormData = {
      ...formData,
      recaptcha: value,
    };
    setFormData(updatedFormData);
  }


  return (
    <>
      <div role="status" aria-live="polite" style={styles.srOnly}>
        {formSuccess && window.gettext('You have successfully signed up.')}
        </div>
  
      {formSuccess ? (
        // successRef lets the effect above find and focus the thank-you heading.
        <span ref={successRef} dangerouslySetInnerHTML={{
          __html: window.gettext('<h3>Thanks for Signing Up</h3><p>We are so glad you joined.</p><p>You are part of community of tens of thousands of women seeking the Lord together. Our desire is that you experience the freedom, fullness, and fruitfulness that can only be found in Christ.</p>')
        }} />
      ) : (
        <>
      {displayH3 && <h3>
        <span dangerouslySetInnerHTML={{
          __html: subscribeCopy.headingHtml
        }} />
      </h3>}
      
      {/* Always rendered with role="alert" so screen readers announce server errors as soon as they appear. */}
      <div role="alert">
        {formErrors.length > 0 && <ul style={styles.formErrors}>
          {formErrors.map((error, index) => (
          <li key={index}>
            {/* Translated prefix; the colon is in the msgid so each language can punctuate it (e.g. French "Erreur :"). */}
            {window.gettext("Error:")} {error}
          </li>
          ))}
        </ul>
        }
      </div>
      <form className='dynamic-labels'>
      <div className="form-group-row form-group-row--three">
        {/* The "error" class, as on the donate form, outlines the field while it has an error. */}
        <div className={`form-group mb-0 _form_element _x03066740 ${fieldErrors.firstname ? "error" : ""}`}>
          <DynamicLabelInput
            labelText={firstNameLabel}
            type="text"
            name="firstname"
            id="firstname"
            autoComplete="given-name"
            value={formData.firstname}
            onChange={handleChange}
            // Validates the field when the user leaves it.
            onBlur={handleBlur}
            // Flags the input as invalid and links it to its message while an error is showing.
            ariaInvalid={!!fieldErrors.firstname}
            ariaDescribedBy={fieldErrors.firstname ? firstNameErrorId : undefined}
          />
          {/* Always rendered so screen readers announce the message when it appears; uses the donate form's errorlist markup. */}
          <div id={firstNameErrorId} aria-live="polite">
            {fieldErrors.firstname && <ul className="errorlist"><li>{fieldErrors.firstname}</li></ul>}
          </div>
        </div>
        {/* Same pattern as the name field. */}
        <div className={`form-group mb-0 _form_element _x95938857 ${fieldErrors.e_mail ? "error" : ""}`}>
          <DynamicLabelInput
            // Keeps the German "E-Mail" label; otherwise the shared translated label.
            labelText={subscribeCopy.emailLabel || emailLabel}
            type="email"
            name="e_mail"
            id="e_mail"
            autoComplete="email"
            value={formData.e_mail}
            onChange={handleChange}
            // Validates the field when the user leaves it.
            onBlur={handleBlur}
            // Flags the input as invalid and links it to its message while an error is showing.
            ariaInvalid={!!fieldErrors.e_mail}
            ariaDescribedBy={fieldErrors.e_mail ? emailErrorId : undefined}
          />
          {/* Always rendered so screen readers announce the message when it appears; uses the donate form's errorlist markup. */}
          <div id={emailErrorId} aria-live="polite">
            {fieldErrors.e_mail && <ul className="errorlist"><li>{fieldErrors.e_mail}</li></ul>}
          </div>
        </div>
        {/* maxHeight stops the button stretching taller than its default height when an error message makes the row taller. */}
        {!displayLists && <button onClick={signupClickHandler} className="btn select" disabled={signupDisabled} style={styles.signupButton}>{gettext("Sign Up")} <svg className="icon" width="14" height="14"><use xlinkHref="#check"/></svg></button>}
      </div>
      </form>
      {displayLists && <div>
        <div className="mb-1 _form_element _x78356968 _full_width">
          {subscribeCopy.updatesHtml && 
          <p>
            <span dangerouslySetInnerHTML={{
              __html: subscribeCopy.updatesHtml
            }} />
          </p>
          }
          <SubscribeListContainer onChange={handleCheckboxChange} />          
          <div style={styles.agreementContainer}>
            <div className="_row _checkbox-radio">
              {/* aria-invalid and aria-describedby flag the checkbox and link it to its message while unchecked. */}
              <input id="field_38" type="checkbox" name="permission" value="permission" checked={formData.permission} style={styles.agreementInput} onChange={handleChange} aria-invalid={fieldErrors.permission ? "true" : undefined} aria-describedby={fieldErrors.permission ? permissionErrorId : undefined} />

              <label style={styles.agreementLabel} htmlFor="field_38">
                <span dangerouslySetInnerHTML={{
                  __html: subscribeCopy.agreementHtml
                }} />
              </label>
              {/* Always rendered so screen readers announce the message; placed after the label, as on the donate form's checkboxes. */}
              <div id={permissionErrorId} aria-live="polite" style={styles.agreementError}>
                {fieldErrors.permission && <ul className="errorlist"><li>{fieldErrors.permission}</li></ul>}
              </div>
            </div>
          </div>
        </div>
        <ReCAPTCHA
          ref={reCaptchaRef}
          sitekey={reCaptchaPublicKey}
          onChange={onChangeReCaptcha}
          theme={darkMode ? "dark" : "light"}
        />
        <div className="mb-1 mt-1 _button-wrapper _full_width">
          <button id="_form_66_submit" className="_submit btn btn-primary btn-lg" type="submit" disabled={signupDisabled} onClick={formSubmitHandler}>{window.gettext('Subscribe Now')}</button>
        </div>
      </div>}
      </>
      )}
    </>
  );
}

export default SubscribeForm;

const styles = {
  formErrors: {
    borderColor: "red",
    color: "red",
  },
  agreementContainer: {
    borderTop: "1px solid var(--border-color)", 
    paddingTop: "10px", 
    marginTop: "10px",
  },
  agreementLabel: {
    display: "block",
    marginLeft: "33px", 
    fontWeight: "normal"
  },
  // Caps the Sign Up button at its default height (matches the inputs' height).
  signupButton: {
    maxHeight: "55px",
  },
  agreementInput: {
    position: "absolute",
  },
  // Lines the consent error up with the label text.
  agreementError: {
    marginLeft: "33px",
  },
  srOnly: {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: "0",
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: "0",
  }
};
